import React, { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ADSENSE_CLIENT, ensureAdsenseLoaded } from "@/lib/adsenseLoader";
import { isAdsenseAllowedPath } from "@/lib/adsensePolicy";

const warnedPlaceholderSlots = new Set();

const minHeightByFormat = {
  horizontal: 90,
  leaderboard: 90,
  rectangle: 250,
  auto: 280,
};

export default function AdSenseUnit({
  slot,
  format = "auto",
  layout,
  layoutKey,
  responsive = true,
  style = {},
  className = "",
}) {
  const location = useLocation();
  const insRef = useRef(null);
  const pushedRef = useRef(false);
  const client = ADSENSE_CLIENT;
  const routeKey = useMemo(() => `${location.pathname}-${slot}-${format}`, [location.pathname, slot, format]);
  const adsAllowed = isAdsenseAllowedPath(location.pathname, location.search);
  const reservedMinHeight = style.minHeight || minHeightByFormat[format] || minHeightByFormat.auto;

  useEffect(() => {
    if (import.meta.env.DEV && slot?.startsWith("0000000") && !warnedPlaceholderSlots.has(slot)) {
      warnedPlaceholderSlots.add(slot);
      console.warn(`[Marstick AdSense] Slot ${slot} is a placeholder — replace in src/config/adSlots.js.`);
    }
  }, [slot]);

  useEffect(() => {
    if (!slot || !adsAllowed || typeof window === "undefined") return undefined;

    pushedRef.current = false;
    let cancelled = false;
    let observer;

    const pushWhenReady = async () => {
      const element = insRef.current;
      if (!element || cancelled || pushedRef.current) return;
      if (element.clientWidth <= 0) return;
      if (element.getAttribute("data-adsbygoogle-status")) return;

      const loaded = await ensureAdsenseLoaded();
      if (!loaded || cancelled || pushedRef.current) return;

      try {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
        pushedRef.current = true;
      } catch (error) {
        console.warn("[AdSense] Failed to push ad slot:", error);
      }
    };

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => pushWhenReady());
      if (insRef.current) observer.observe(insRef.current);
    }

    requestAnimationFrame(pushWhenReady);

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [routeKey, slot, adsAllowed]);

  if (!slot || !adsAllowed) return null;

  return (
    <div className={className} key={routeKey} style={{ minHeight: reservedMinHeight }}>
      <ins
        className="adsbygoogle"
        style={{ display: "block", minHeight: reservedMinHeight, ...style }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? "true" : "false"}
        {...(layout ? { "data-ad-layout": layout } : {})}
        {...(layoutKey ? { "data-ad-layout-key": layoutKey } : {})}
        ref={insRef}
      />
    </div>
  );
}